import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import Tooltip from '@ui/atoms/Tooltip';

import aligned from '@ui/styled/Aligned';
import wrapped from '@ui/styled/Wrapped';

import theme from '@theme/FolxTheme';

interface ITopicCardProps {
	topic: string;
	disabled: boolean;
	tooltipDescription: string;
	flow: string;
	id: number;
	mhId?: string;
	onClick?: (args: {
		topic: string;
		flow: string;
		id: number;
		mhId?: string;
	}) => React.MouseEventHandler<HTMLElement>;
	showTooltip?: boolean;
}

interface ChildrenProps {
	children: ReactNode;
}

const TopicCardLayout = styled(BaseCard)<{ disabled: boolean }>`
	border-radius: ${theme.radius.xl};
	box-shadow: ${theme.shadow.sliderBox};
	background-color: ${({ disabled }) => disabled && theme.colors.gray};
`;

const TopicText = styled(BasicText)`
	font-size: ${theme.fontSizes.body.sm};
`;
const UnavailableLabelText = styled(BasicText)`
	font-size: ${theme.fontSizes.body.smmd};
`;

const AlignedColumn = aligned(FlexColumn);
const AlignedRow = aligned(FlexRow);

const StyledAlignedColumn = styled(AlignedColumn)`
	padding: ${theme.spacing.xssm};
	text-align: start;
	width: 16rem;
	height: 9.4rem;
	cursor: pointer;
`;

const TopicWrapper = wrapped(styled(FlexColumn)`
	padding: ${theme.spacing.xssm};
	width: 16rem;
	height: 9.4rem;
`);

const UnstyledButton = styled.button`
	all: unset;
	cursor: pointer;
	position: relative;

	:focus {
		outline: 5px auto Highlight;
		outline: 5px auto -webkit-focus-ring-color;
	}
`;

const TooltipWrapper = styled.div`
	position: absolute;
	top: 0;
	right: 0;
`;

const TopicCard: FC<ITopicCardProps> = ({
	topic,
	flow = '',
	id,
	mhId = '',
	disabled,
	tooltipDescription = '',
	showTooltip = false,
	onClick = ({ topic, flow, id, mhId }) =>
		(e) => {
			return;
		},
}) => {
	const TopicCardButton: FC<ChildrenProps> = ({ children }) => (
		<UnstyledButton onClick={onClick({ topic, flow, id, mhId })} disabled={disabled}>
			{children}
		</UnstyledButton>
	);

	return (
		<TopicCardLayout disabled={disabled} radius="md" bgColor="white" borderless>
			<TopicCardButton>
				<StyledAlignedColumn
					gap="xxs"
					alignItems="start"
					justifyContent={disabled ? 'space-between' : 'end'}>
					{disabled && (
						<AlignedRow gap="xxs" alignItems="center" justifyContent="start">
							<UnavailableLabelText>Unavailable</UnavailableLabelText>
							<Tooltip
								description={tooltipDescription}
								placement="bottom-end"
								fillColor="darkGray"
								defaultIcon
							/>
						</AlignedRow>
					)}
					{showTooltip && !disabled && (
						<TooltipWrapper>
							<Tooltip
								description={tooltipDescription}
								placement="bottom-end"
								fillColor="darkGray"
								defaultIcon
							/>
						</TooltipWrapper>
					)}
					<TopicText>{topic}</TopicText>
				</StyledAlignedColumn>
			</TopicCardButton>
		</TopicCardLayout>
	);
};

export default styled(TopicCard)``;
